import { httpClient } from 'providers/clients/httpClient';

/**
 * Dynamic Fields Spec
 * https://townnews.atlassian.net/wiki/spaces/COSMOS/pages/2881847298/
 * typegen: components['schemas']['core.field.spec.field']
 *
 * UI Components and Relevant Types:
 * /src/components/DynamicFormField/...
 *
 */

export type CoreFieldEnumOpt = {
  value: string;
  label: string;
  help?: string;
  icon?: string | null;
  disabled?: boolean;
  [key: string]: any;
};

type GetDynamicEnumerationDataParams = {
  app?: string;
  resolver?: string;
  limit?: number;
  page?: string | number;
  params?: Record<string, any>;
};

type GetDynamicEnumerationDataResponse = {
  status?: string | number;
  data: {
    total: number;
    next: string | number;
    prev: string | number;
    items: CoreFieldEnumOpt[];
  };
};

export type CoreFieldDtreeNode = {
  id: number;
  label: string;
  path: string;
  readonly: boolean;
  leaf: boolean;
  value: string;
  help: string | null;
  icon: string | null;
  [key: string]: any;
};

type GetDynamicTreeDataParams = {
  app?: string;
  resolver?: string;
  parent?: string;
  params?: Record<string, any>;
};

export type GetDynamicTreeDataResponse = {
  status?: string | number;
  data: {
    total: number;
    next: string | number;
    prev: string | number;
    items: CoreFieldDtreeNode[];
  };
};

export const dynamicFieldDataProvider = {
  // Retrieve dynamic enumeration data for a field
  getDynamicEnumerationData: async ({
    app,
    resolver,
    limit = 10,
    page = 0,
    params = {},
  }: GetDynamicEnumerationDataParams): Promise<GetDynamicEnumerationDataResponse> => {
    try {
      // Build the query string with limit, page, and additional params
      const queryParams = {
        limit: limit.toString(),
        page: page.toString(),
        ...params,
      };
      const queryString = new URLSearchParams(queryParams).toString();

      const url = `/core/field/denum/${app}/${resolver}/?${queryString}`;

      const { data, status } = await httpClient({
        url: url,
        method: 'GET',
      });

      const result = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getDynamicEnumerationData] Error', error);
      throw error;
    }
  },

  // Retrieve dynamic tree data for a field
  getDynamicTreeData: async ({
    app,
    resolver,
    parent,
    params = {},
  }: GetDynamicTreeDataParams): Promise<GetDynamicTreeDataResponse> => {
    try {
      // Ensure parent defaults to ',' if not provided.
      const resolvedParent = parent || ',';
      const queryParams = { ...params };
      const queryString = new URLSearchParams(queryParams).toString();

      const url = `/core/field/dtree/${app}/${resolver}/${resolvedParent}/${
        queryString ? `?${queryString}` : ''
      }`;

      const { data, status } = await httpClient({
        url: url,
        method: 'GET',
      });

      const result = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getDynamicTreeData] Error', error);
      throw error;
    }
  },
  // set as const - all keys (functions) in this object
  // are readonly and we should to ensure they
  // are immutable / cant be accidentally modified
} as const;

export type DynamicFieldProviderType = typeof dynamicFieldDataProvider;
